/**
 * 場後結算 · 前端純函式（P1-10；`ui-spec` 頁 12）
 *
 * 為何住 `lib/`：頁面／元件要**共用同一份**轉換（輸入狀態 ↔ 模組模型 ↔ API 線格式），
 * 而且元件檔有 ≤250 行上限（硬規則 B）—— 擺入 `ReconcileRow.tsx` 會爆。
 *
 * 紅線（前端只係提示；**後端 ＋ 模組才係權威**）
 *   · ⚠️ 對數（`G1`）**一律**用 `@coeldery/event-reconciliation` 嘅 `lineErrors()`／`toUnits()`；
 *     本檔**只做**「格 ↔ 模型」搬字過紙，**唔准**加任何驗證（R1：規則只得一個實作）
 *   · ⚠️ 落點（`roadshow:<單號>`／`store:<code>`…）**唔准**自己駁字串（由後端模組計）
 */
import type {
  Qty,
  ReconcileLine as ReconcileModel,
  ReconcileLineContext,
} from '@coeldery/event-reconciliation';
import type {
  ReconcileBaseLine,
  ReconcileInputLine,
  ReconcileLine as ReconcileDbLine,
} from './api';

/** 一行嘅輸入狀態（全部係字串：保留輸入過程；空 ＝ 未填） */
export interface RowState {
  soldCartons: string;
  soldUnits: string;
  leftoverCartons: string;
  leftoverUnits: string;
  damagedCartons: string;
  damagedUnits: string;
  dispositionCode: string;
  targetOrderNo: string;
  targetStoreCode: string;
  note: string;
}

/** 空行（未填 ＝ 0 —— 但四項**全部要填過**才對得上，見模組 `G1`） */
export const emptyRow = (): RowState => ({
  soldCartons: '',
  soldUnits: '',
  leftoverCartons: '',
  leftoverUnits: '',
  damagedCartons: '',
  damagedUnits: '',
  dispositionCode: '',
  targetOrderNo: '',
  targetStoreCode: '',
  note: '',
});

/** 已存結算行 → 輸入狀態（重新載入／改草稿用） */
export const rowFromDb = (line: ReconcileDbLine | undefined): RowState => {
  if (!line) return emptyRow();
  const s = (v: number): string => (v === 0 ? '' : String(v));
  return {
    soldCartons: s(line.soldCartons),
    soldUnits: s(line.soldUnits),
    leftoverCartons: s(line.leftoverCartons),
    leftoverUnits: s(line.leftoverUnits),
    damagedCartons: s(line.damagedCartons),
    damagedUnits: s(line.damagedUnits),
    dispositionCode: line.dispositionCode ?? '',
    targetOrderNo: line.targetOrderNo ?? '',
    targetStoreCode: line.targetStoreCode ?? '',
    note: line.note ?? '',
  };
};

/** 空 ＝ 0；亂碼 ＝ `NaN`（交返畀模組報 `qty_invalid` —— 前端**唔**驗數量） */
const num = (v: string): number => (v.trim() === '' ? 0 : Number(v));
const qty = (cartons: string, units: string): Qty => ({ cartons: num(cartons), units: num(units) });
const orNull = (v: string): string | null => v.trim() || null;

/** 輸入狀態 → 模組要嘅 `ReconcileLine`（**唯一**轉換點：表單、即時驗證、摘要共用） */
export const rowToLine = (base: ReconcileBaseLine, r: RowState): ReconcileModel => ({
  outboundLineId: base.lineId,
  itemId: base.itemId,
  batchId: base.batchId,
  sold: qty(r.soldCartons, r.soldUnits),
  leftover: qty(r.leftoverCartons, r.leftoverUnits),
  damaged: qty(r.damagedCartons, r.damagedUnits),
  dispositionCode: orNull(r.dispositionCode),
  targetOrderNo: orNull(r.targetOrderNo),
  targetStoreCode: orNull(r.targetStoreCode),
  note: orNull(r.note),
});

/** 基準行 → 模組要嘅驗證內文（`outCartons` ＝ **實際執到**；冇執貨單一律 0） */
export const reconcileContexts = (
  base: ReconcileBaseLine[],
  orderNo: string,
): ReconcileLineContext[] =>
  base.map((b) => ({
    outboundLineId: b.lineId,
    itemId: b.itemId,
    outCartons: b.outCartons,
    unitsPerCarton: b.unitsPerCarton,
    orderNo,
  }));

/** 模組模型 → API 線格式（存檔用；⚠️ 帳目唔喺呢度出 —— 由後端確認時產生） */
export const toInputLines = (models: ReconcileModel[]): ReconcileInputLine[] =>
  models.map((l) => ({
    lineId: l.outboundLineId,
    soldCartons: l.sold.cartons,
    soldUnits: l.sold.units,
    leftoverCartons: l.leftover.cartons,
    leftoverUnits: l.leftover.units,
    damagedCartons: l.damaged.cartons,
    damagedUnits: l.damaged.units,
    dispositionCode: l.dispositionCode,
    targetOrderNo: l.targetOrderNo,
    targetStoreCode: l.targetStoreCode,
    note: l.note,
  }));
